import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import {
  getShipmentPriceBoxes,
  type ShipmentPriceBox,
} from 'common/src/api/shipment-price-boxes';
import RegisterPricesFlow from 'common/src/components/market/RegisterPricesFlow';
import { useTranslation } from 'common/src/hooks/useTranslation';
import type { BoxFormatMapping, Shipment } from 'common/src/utils/types';
import { Alert, EmptyState, KpiCard, LoadingState, SectionTitle } from '../ui';
import { summarizeShipmentPrices } from '../../utils/shipment-price-summary';

interface ShipmentMarketPricesProps {
  shipment: Shipment;
  user: { id: number; name: string };
  isOpen: boolean;
  isRegistering: boolean;
  onRegisteringChange: (open: boolean) => void;
  onBusyChange: (busy: boolean) => void;
  onActivityChanged?: () => void;
  onFormatMapped?: (mapping: BoxFormatMapping) => void;
}

export default function ShipmentMarketPrices({
  shipment,
  user,
  isOpen,
  isRegistering,
  onRegisteringChange,
  onBusyChange,
  onFormatMapped,
  onActivityChanged,
}: ShipmentMarketPricesProps) {
  const { t, currentLanguage } = useTranslation('shipments');
  const [boxes, setBoxes] = useState<ShipmentPriceBox[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const mounted = useRef(false);
  const requestVersion = useRef(0);
  const { id, exporter_id, season, species_id } = shipment;
  const loadSummary = useCallback(async () => {
    const version = ++requestVersion.current;
    const isCurrent = () =>
      mounted.current && requestVersion.current === version;
    setIsLoading(true);
    setHasError(false);
    setBoxes([]);
    try {
      const rows = await getShipmentPriceBoxes(
        id,
        exporter_id,
        season,
        species_id
      );
      if (isCurrent()) setBoxes(rows);
    } catch {
      if (isCurrent()) setHasError(true);
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [id, exporter_id, season, species_id]);

  useEffect(() => {
    mounted.current = true;
    void loadSummary();
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
    };
  }, [loadSummary]);

  const handleFormatMapped = (mapping: BoxFormatMapping) => {
    const code = mapping.frutic_code.trim().toUpperCase();
    setBoxes((current) =>
      current.map((box) =>
        (box.box_type || '').trim().toUpperCase() === code
          ? { ...box, box_format: mapping.cherry_box_format }
          : box
      )
    );
    onFormatMapped?.(mapping);
  };
  const summary = useMemo(() => summarizeShipmentPrices(boxes), [boxes]);
  const locale =
    currentLanguage === 'es'
      ? 'es-CL'
      : currentLanguage === 'zh'
        ? 'zh-CN'
        : 'en-US';
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(value);
  const date = (value: string) => {
    if (!value) return '—';
    const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
    return Number.isNaN(parsed.getTime())
      ? value
      : new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }).format(parsed);
  };

  return (
    <div className='flex flex-col gap-4 pt-4'>
      {isRegistering ? (
        <RegisterPricesFlow
          presentation='embedded'
          isOpen={isOpen}
          initialShipment={shipment}
          exporterId={exporter_id}
          speciesId={species_id}
          season={season}
          userId={user.id}
          userName={user.name}
          onClose={() => onRegisteringChange(false)}
          onSaved={() => {
            onActivityChanged?.();
            void loadSummary();
          }}
          onBusyChange={onBusyChange}
          onFormatMapped={handleFormatMapped}
        />
      ) : (
        <>
          <SectionTitle
            title={t('Precios informados')}
            description={t('Precios por bulto registrados para este despacho.')}
            actions={
              <Button
                color='primary'
                startContent={<Icon icon='tabler:plus' width={18} />}
                onPress={() => onRegisteringChange(true)}
                isDisabled={isLoading || hasError || boxes.length === 0}
              >
                {t('Registrar precios')}
              </Button>
            }
          />
          {isLoading ? (
            <LoadingState label={t('Cargando precios...')} />
          ) : hasError ? (
            <Alert variant='danger' showIcon>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <span>
                  {t('No se pudieron cargar los precios del despacho.')}
                </span>
                <Button
                  size='sm'
                  variant='flat'
                  onPress={() => void loadSummary()}
                >
                  {t('Reintentar')}
                </Button>
              </div>
            </Alert>
          ) : (
            <>
              <div className='grid grid-cols-3 gap-3'>
                <KpiCard
                  label={t('Bultos totales')}
                  value={number(summary.totalQuantity)}
                />
                <KpiCard
                  label={t('Bultos con precio')}
                  value={number(summary.pricedQuantity)}
                />
                <KpiCard
                  label={t('Bultos pendientes')}
                  value={number(summary.pendingQuantity)}
                />
              </div>
              {summary.groups.length === 0 ? (
                <EmptyState
                  icon='tabler:report-money'
                  title={t('Sin precios informados')}
                  description={
                    boxes.length > 0
                      ? t(
                          'Registra los precios de venta de los productos de este despacho.'
                        )
                      : t('Este despacho todavía no tiene bultos disponibles.')
                  }
                />
              ) : (
                <Table
                  isStriped
                  isCompact
                  aria-label={t('Precios informados del despacho')}
                >
                  <TableHeader>
                    <TableColumn className='min-w-40'>
                      {t('Producto')}
                    </TableColumn>
                    <TableColumn align='end' className='text-right'>
                      {t('Bultos')}
                    </TableColumn>
                    <TableColumn align='end' className='text-right'>
                      {t('Precio por bulto')}
                    </TableColumn>
                    <TableColumn>{t('Fecha de venta')}</TableColumn>
                  </TableHeader>
                  <TableBody items={summary.groups}>
                    {(group) => (
                      <TableRow key={group.key}>
                        <TableCell className='min-w-40'>
                          <span className='font-medium'>
                            {[group.variety, group.caliber]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </span>
                          <p className='text-xs text-default-500'>
                            {[
                              group.format || t('Sin formato'),
                              group.weight == null
                                ? null
                                : `${number(group.weight)} kg`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </TableCell>
                        <TableCell className='text-right'>
                          {number(group.quantity)}
                        </TableCell>
                        <TableCell className='whitespace-nowrap text-right'>
                          {number(group.price)}{' '}
                          {group.currency || t('Sin moneda')}
                        </TableCell>
                        <TableCell className='whitespace-nowrap'>
                          {date(group.saleDate)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
